import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Upload, X, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReportProblemPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: ''
  });
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/report-problem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Problema reportado com sucesso! Nossa equipe irá analisar e entrar em contato em breve.', {
          duration: 5000,
        });
        setFormData({ name: '', email: '', phone: '', description: '' });
        setFiles([]);
      } else {
        toast.error(data.detail || 'Erro ao enviar. Tente novamente.', {
          duration: 5000,
        });
      }
    } catch (error) {
      toast.error('Erro de conexão. Tente novamente mais tarde.', {
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles].slice(0, 3)); // Max 3 files
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-dark-primary py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-purple-light hover:text-purple-primary transition-colors mb-8">
          <ArrowLeft className="mr-2" size={20} />
          Voltar para Home
        </Link>

        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-orange-500 rounded-full mb-6">
            <AlertCircle className="text-white" size={40} />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-archive text-white mb-4 glow-text">
            Reportar Problema
          </h1>
          <p className="text-xl text-gray-400 font-hero max-w-2xl mx-auto">
            Encontrou algum problema? Descreva detalhadamente para que possamos ajudar
          </p>
        </div>

        {/* Form Card */}
        <Card className="bg-dark-secondary border-purple-primary/30">
          <CardHeader>
            <CardTitle className="text-3xl font-archive text-white">
              Descreva o Problema
            </CardTitle>
            <CardDescription className="text-gray-400 font-hero text-lg">
              Preencha todos os campos para que possamos resolver o problema rapidamente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="problem-name" className="text-white font-hero text-lg">
                  Nome Completo <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="problem-name"
                  name="name"
                  type="text"
                  placeholder="Seu nome completo"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500 focus:border-red-500 text-lg py-6"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="problem-email" className="text-white font-hero text-lg">
                    E-mail <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="problem-email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500 focus:border-red-500 text-lg py-6"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="problem-phone" className="text-white font-hero text-lg">
                    Telefone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="problem-phone"
                    name="phone"
                    type="tel"
                    placeholder="+55 (32) 98494-5548"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500 focus:border-red-500 text-lg py-6"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="problem-description" className="text-white font-hero text-lg">
                  Descrição do Problema <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="problem-description"
                  name="description"
                  placeholder="Descreva o problema em detalhes... Inclua: 
• O que estava fazendo quando o problema ocorreu
• Mensagens de erro (se houver)
• Dispositivo e navegador que está usando
• Qualquer outra informação relevante"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={8}
                  className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500 focus:border-red-500 resize-none text-lg"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-white font-hero text-lg">
                  Anexar Imagens/Documentos <span className="text-gray-500 text-sm">(Opcional - Máx. 3 arquivos)</span>
                </Label>
                
                <div className="border-2 border-dashed border-purple-primary/30 rounded-xl p-8 text-center hover:border-purple-primary/60 transition-colors">
                  <Upload className="mx-auto mb-4 text-purple-light" size={48} />
                  <p className="text-gray-400 font-hero mb-4">
                    Arraste arquivos aqui ou clique para selecionar
                  </p>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-upload').click()}
                    className="border-purple-primary/30 hover:bg-purple-primary/20 text-purple-light"
                  >
                    Selecionar Arquivos
                  </Button>
                </div>

                {files.length > 0 && (
                  <div className="space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-dark-primary/50 p-3 rounded-lg">
                        <span className="text-gray-300 font-hero text-sm truncate flex-1">
                          {file.name}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <X size={18} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:opacity-90 text-white font-bold py-7 text-xl rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar Relatório'
                )}
              </Button>

              <p className="text-center text-gray-500 text-sm font-hero mt-4">
                Nossa equipe irá analisar seu relato e retornar em até 72 horas úteis
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
